import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import { keyframes } from '@emotion/react';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import MainLayout from 'layouts/MainLayout';
import {
  auth,
  googleProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile
} from 'services/firebase';
import storage from 'helpers/storage';
import { setAuthToken, hasAuthToken } from 'helpers/authToken';
import { addError, addMessage } from 'actions/error';
import Message from 'components/Snackbars/Message';
import { setReferralKey } from 'actions/referral';
import { ping } from 'actions/app';
import { resolvePostAuthRedirect } from 'helpers/postAuthRedirect';
import Preloader from 'components/Preloader';
import { handleTransferOnboarding } from 'actions/onboarding';
import EyeIcon from 'assets/icons/eye.svg?react';
import OrIcon from 'assets/icons/orIcon.svg?react';
import LinearProgress from 'components/LinearProgress';
import { getLanguage } from 'translation';
import SignIcon from 'assets/icons/sign_arrow.svg?react';
import { getLocalizedPath } from 'helpers/getLocale';
import getAuthErrorKey from 'helpers/getAuthErrorKey';

const Wrapper = styled('section')(({ theme }) => ({
  width: '400px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  minHeight: 'calc(100vh - 185px)',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '20px',
    minHeight: 'calc(100vh - 135px)'
  }
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: 25,
  marginTop: 60,
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '42px',
    marginBottom: 30,
    marginTop: 30
  }
}));

const TextFieldWrapper = styled(TextField)(() => ({
  marginBottom: 10,
  width: '100%'
}));

const ButtonWrapper = styled(Button)(({ theme }) => ({
  marginBottom: 10,
  borderRadius: 8,
  width: '100%',
  color: theme.palette.textPrimary.main,
  '&:last-of-type': {
    marginBottom: 60
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: 10,
    '&:last-of-type': {
      marginBottom: 30
    }
  }
}));

const OrIconStyled = styled(OrIcon)(() => ({
  width: '100%',
  marginBottom: '40px',
  marginTop: '40px'
}));

const IconButtonToggleVisibility = styled(IconButton)(() => ({
  height: 28,
  position: 'relative'
}));

const TypographyActinoid = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  '& span': {
    cursor: 'pointer',
    marginLeft: 5,
    borderBottom: `1px solid ${theme.palette.textPrimary.main}`
  }
}));

const SuccessTitle = styled(Typography)(({ theme }) => ({
  marginBottom: 16,
  marginTop: 28,
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '42px',
    marginBottom: 12,
    marginTop: 22
  }
}));

const SuccessDescription = styled(Typography)(({ theme }) => ({
  marginBottom: 32,
  maxWidth: 340,
  color: theme.palette.grey[300],
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    lineHeight: '26px',
    marginBottom: 28
  }
}));

// Gentle float for the envelope badge on the "check your email" screen.
const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const EmailBadge = styled('div')(({ theme }) => ({
  width: 112,
  height: 112,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 70,
  animation: `${floatY} 3s ease-in-out infinite`,
  '& svg': {
    fontSize: 54,
    color: theme.palette.primary.main
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: 40,
    width: 96,
    height: 96,
    '& svg': { fontSize: 46 }
  }
}));

const TypographyAgreement = styled(Typography)(({ theme }) => ({
  color: 'rgba(160, 160, 160, 1)',
  fontSize: 16,
  lineHeight: '24px',
  marginTop: 40,
  marginBottom: 20,
  '& span': {
    cursor: 'pointer',
    marginLeft: 5,
    borderBottom: '1px solid rgba(160, 160, 160, 1)'
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: 0,
    marginBottom: 25
  }
}));

const SignIconStyled = styled(SignIcon)(({ theme }) => ({
  position: 'absolute',
  top: '50px',
  right: '-320px',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const SignUpScreen = () => {
  const theme = useTheme();
  const t = useTranslate('SignUp');
  const t2 = useTranslate('HELMET');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [displayName, setDisplayName] = React.useState(() => {
    try {
      const onboarding = storage.getItem('onboarding');
      if (onboarding) return JSON.parse(onboarding).name;
      return '';
    } catch {
      return '';
    }
  });
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [checkEmailScreen, setCheckEmailScreen] = React.useState(false);
  const [isError, setIsError] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  const redirect = useSelector((state) => state.app.localSignInRedirect);
  const [searchParams] = useSearchParams();

  // `?source=pro` (counselor funnel) — tag the registration so the backend
  // grants the consultant role, even if sessionStorage was cleared.
  React.useEffect(() => {
    if (searchParams.get('source') === 'pro') {
      try {
        sessionStorage.setItem('registrationSource', 'pro');
      } catch (_) {
        // sessionStorage unavailable — ignore.
      }
    }
  }, [searchParams]);

  // Read (and consume) an explicit funnel redirect target stashed before the
  // auth round trip (survives a round trip that clears Redux).
  const getExplicitRedirect = React.useCallback(() => {
    const stored = storage.getItem('postAuthRedirect');
    if (stored) storage.removeItem('postAuthRedirect');
    return redirect || stored || '';
  }, [redirect]);

  const setReferral = React.useCallback(async () => {
    const referralKey = storage.getItem('referral');
    if (referralKey) {
      const result = await dispatch(setReferralKey(referralKey));
      if (result instanceof Error) return;
      storage.removeItem('referral');
      dispatch(addMessage(new Message('ReferralSuccess', 'success')));
    }
  }, [dispatch]);

  // Shared post-auth tail: mint the backend session, apply any referral, then
  // route the user (consultants → /pro onboarding, others → /profile).
  const completeAuth = React.useCallback(
    async (user) => {
      const idToken = await user.getIdToken(true);
      await setAuthToken(idToken);
      await setReferral();
      let authedUser = null;
      try {
        const res = await dispatch(ping());
        authedUser = res?.user || null;
      } catch (_) {
        // Non-fatal — fall back to the default redirect.
      }
      const dest = await resolvePostAuthRedirect(getExplicitRedirect(), authedUser, dispatch);
      navigate(getLocalizedPath(dest));
      handleTransferOnboarding(dispatch);
    },
    [dispatch, navigate, setReferral, getExplicitRedirect]
  );

  const handleGoogleSignIn = React.useCallback(async () => {
    if (loading) return;

    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      await completeAuth(result.user);
    } catch (error) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        dispatch(addError(new Error(getAuthErrorKey(error))));
      }
      setLoading(false);
    }
  }, [completeAuth, dispatch, loading]);

  const handleEmailSignUp = React.useCallback(
    async (e) => {
      e.preventDefault();
      if (loading) return;
      try {
        setIsError({});

        if (!email || !password || !displayName) {
          setIsError({
            email: !email,
            password: !password,
            displayName: !displayName
          });
          return;
        }

        setLoading(true);

        const { user } = await createUserWithEmailAndPassword(auth, email, password);

        await updateProfile(user, {
          displayName,
          languageCode: getLanguage().toLowerCase()
        });

        // The account already exists at this point — a failure to send the
        // verification email must not strand the user. Advance regardless and
        // surface a non-blocking notice; verification can be re-requested.
        try {
          await sendEmailVerification(user);
        } catch (verificationError) {
          dispatch(addMessage(new Message('AccountCreatedVerificationFailed', 'warning')));
        }

        setLoading(false);

        setCheckEmailScreen(true);
      } catch (error) {
        dispatch(addError(new Error(getAuthErrorKey(error))));
        setLoading(false);
      }
    },
    [dispatch, email, password, displayName, loading]
  );

  const handlePasswordVisibility = React.useCallback(() => {
    setIsPasswordVisible(!isPasswordVisible);
  }, [isPasswordVisible]);

  // Already-authenticated visitors are bounced to /dashboard, which resolves
  // the role and forwards to the user or counselor dashboard.
  React.useEffect(() => {
    if (hasAuthToken() || storage.getItem('token')) {
      navigate(getLocalizedPath('/dashboard'));
    }
  }, [navigate]);

  // Authenticated users are redirecting away (effect above) — show the
  // preloader rather than briefly flashing the sign-up form.
  if (hasAuthToken() || storage.getItem('token')) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet
        meta={{
          title: t2('signUpTitle'),
          description: t2('signUpDescription'),
          ogTitle: t2('signUpOgTitle'),
          ogDescription: t2('signUpOgDescription')
        }}
      />
      <MainLayout>
        <Header />
        <Wrapper>
          {checkEmailScreen ? (
            <>
              <EmailBadge>
                <MailOutlineRoundedIcon />
              </EmailBadge>
              <SuccessTitle variant="h1">{t('CheckEmailTitle')}</SuccessTitle>
              <SuccessDescription variant="body1">
                {t('CheckEmailDescription')}
              </SuccessDescription>
              <ButtonWrapper
                onClick={() => navigate(getLocalizedPath('/sign-in'))}
                variant="contained"
                color="primary"
                aria-label={t('SignIn')}>
                {t('SignIn')}
              </ButtonWrapper>
            </>
          ) : (
            <>
              <StyledTitle variant="h1">{t('Title')}</StyledTitle>

              <TextFieldWrapper
                placeholder={t('Name')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                fullWidth={true}
                error={isError.displayName}
                helperText={isError.displayName ? t('RequiredField') : ''}
              />

              <TextFieldWrapper
                placeholder={t('Email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth={true}
                error={isError.email}
                helperText={isError.email ? t('RequiredField') : ''}
              />

              <TextFieldWrapper
                placeholder={t('Password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth={true}
                type={isPasswordVisible ? 'text' : 'password'}
                error={isError.password}
                helperText={isError.password ? t('RequiredField') : ''}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButtonToggleVisibility
                        size="small"
                        sx={
                          isPasswordVisible
                            ? {
                                '&:before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: '4px',
                                  left: '13px',
                                  width: '1px',
                                  height: '21px',
                                  backgroundColor: theme.palette.grey[500],
                                  transform: 'rotate(-45deg)'
                                }
                              }
                            : null
                        }
                        onClick={handlePasswordVisibility}>
                        <EyeIcon />
                      </IconButtonToggleVisibility>
                    )
                  }
                }}
              />

              <ButtonWrapper
                onClick={handleEmailSignUp}
                variant="contained"
                color="primary"
                aria-label={t('SignUp')}>
                {t('SignUp')}
              </ButtonWrapper>

              <TypographyActinoid variant="body2">
                {t('ToLogin')}
                <span onClick={() => navigate(getLocalizedPath('/sign-in'))}>{t('SignIn')}</span>
              </TypographyActinoid>

              <OrIconStyled />

              <ButtonWrapper
                onClick={handleGoogleSignIn}
                variant="outlined"
                color="primary"
                aria-label={t('GoogleAuth')}>
                {t('GoogleAuth')}
              </ButtonWrapper>

              <TypographyAgreement>
                {t('TermsText')}
                <span onClick={() => navigate(getLocalizedPath('/policy'))}>{t('TermsLink')}</span>
              </TypographyAgreement>

              {loading ? <LinearProgress /> : null}
            </>
          )}
          <SignIconStyled />
        </Wrapper>
        <Footer />
      </MainLayout>
    </>
  );
};

export default SignUpScreen;
